/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import "@/index.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Funnel from "@/pages/Funnel";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import ProtectedWorkspace from "@/components/ProtectedWorkspace";
import DirectoryIndex from "@/pages/DirectoryIndex";
import DirectoryNiche from "@/pages/DirectoryNiche";
import DirectoryBranch from "@/pages/DirectoryBranch";
import AdminOnlyRoute from "@/components/AdminOnlyRoute";

// Code-split heavy off-critical-path routes to shrink the initial mobile
// bundle. Landing (/), directory pages, and auth stay in the entry chunk
// so they render on first paint without a network round-trip.
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Workspace = lazy(() => import("@/pages/Workspace"));
const SecretEngine = lazy(() => import("@/pages/SecretEngine"));
const Franchise = lazy(() => import("@/pages/Franchise"));
const Demo = lazy(() => import("@/pages/Demo"));
const BusinessProfile = lazy(() => import("@/pages/BusinessProfile"));
const ROICalculator = lazy(() => import("@/pages/ROICalculator"));
const BuyerIntentLanding = lazy(() => import("@/pages/BuyerIntentLanding"));

// Lightweight skeleton shown while a lazy chunk streams in. Matches the
// dark theme so it doesn't flash white.
function RouteFallback() {
    return (
        <div
            role="progressbar"
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading page"
            className="min-h-screen bg-background flex items-center justify-center"
            data-testid="route-lazy-fallback"
        >
            <div className="flex flex-col items-center gap-3 text-neutral-400">
                <div
                    className="w-8 h-8 border-2 border-neutral-700 border-t-emerald-400 rounded-full animate-spin"
                    aria-hidden="true"
                />
                <div className="text-xs font-mono uppercase tracking-widest">Loading…</div>
            </div>
        </div>
    );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function ProtectedWorkspaceRoute() {
    const { token } = useParams();
    return (
        <ProtectedWorkspace token={token}>
            <Workspace />
        </ProtectedWorkspace>
    );
}

function AppRouter() {
    const location = useLocation();
    // Custom-domain host-based routing. When a visitor lands on an
    // operator's custom domain (e.g. myreliablecleanup.com) at the ROOT
    // path, we look up who owns the hostname and swap the render to
    // their /business/:slug page — the URL bar keeps the custom domain
    // but the SPA renders the branded landing.
    const [customSlug, setCustomSlug] = useState(null);
    const [hostChecked, setHostChecked] = useState(false);
    useEffect(() => {
        const host = window.location.hostname.toLowerCase();
        const isReserved =
            host === "localhost" ||
            host.endsWith(".emergentagent.com") ||
            host.endsWith(".emergentagent.net") ||
            host === "fixmysitekit.com" ||
            host === "www.fixmysitekit.com" ||
            host === "speedtolead.com" ||
            host === "www.speedtolead.com";
        if (isReserved || location.pathname !== "/") {
            setHostChecked(true);
            return;
        }
        const api = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${api}/api/business/by-hostname?host=${encodeURIComponent(host)}`)
            .then((r) => (r.ok ? r.json() : { ok: false }))
            .then((d) => { if (d.ok && d.slug) setCustomSlug(d.slug); })
            .catch(() => {})
            .finally(() => setHostChecked(true));
    }, [location.pathname]);

    // Synchronous fragment detection — useEffect would be too late and race with /auth/me
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    // Custom domain override: render BusinessProfile with the resolved slug
    if (customSlug) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <CustomDomainBusinessProfile slug={customSlug} />
            </Suspense>
        );
    }
    // While we check, keep the UI blank on the root path so the funnel
    // doesn't flash before the redirect kicks in. Any non-root path skips
    // this and renders normally.
    if (!hostChecked && location.pathname === "/") {
        return <RouteFallback />;
    }
    return (
        <Suspense fallback={<RouteFallback />}>
            <Routes>
                <Route path="/" element={<Funnel />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/secure-ops-hub-99x/:token" element={<ProtectedWorkspaceRoute />} />
                {/* Hidden SEO directory — not linked from the funnel */}
                <Route path="/solutions" element={<DirectoryIndex />} />
                <Route path="/solutions/:niche" element={<DirectoryNiche />} />
                <Route path="/solutions/:niche/:location" element={<DirectoryBranch />} />
                <Route path="/business/:slug" element={<BusinessProfile />} />
                <Route
                    path="/as7pse7act2h9qk6"
                    element={<AdminOnlyRoute><SecretEngine /></AdminOnlyRoute>}
                />
                <Route path="/license" element={<Franchise />} />
                <Route path="/franchise" element={<Navigate to="/license" replace />} />
                <Route path="/roi-calculator" element={<ROICalculator />} />
                <Route path="/website-for/:niche/:metro" element={<BuyerIntentLanding />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
}

// Small wrapper that pushes the resolved /business/:slug URL when a
// visitor lands on a mapped custom domain root. The Route below then
// renders BusinessProfile normally with the correct params.
function CustomDomainBusinessProfile({ slug }) {
    return (
        <Routes>
            <Route path="/" element={<Navigate to={`/business/${slug}`} replace />} />
            <Route path="/business/:slug" element={<BusinessProfile />} />
            <Route path="*" element={<Navigate to={`/business/${slug}`} replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App min-h-screen bg-background text-foreground">
            <BrowserRouter>
                <AppRouter />
            </BrowserRouter>
            <Toaster richColors theme="dark" position="top-right" />
        </div>
    );
}

export default App;
